import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import {
	BehaviorSubject,
	firstValueFrom,
	Observable,
	of,
	takeUntil,
	tap,
	timer,
} from 'rxjs';

import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZES } from '../../../core/dx-types';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	initFormulaList,
	openDeleteConfirmationPopup,
	openDuplicateConfirmationPopup,
	refreshFormulaList,
	toggleFormulaSharedStatus,
} from '../../store/formula.actions';
import { FormulaSelector } from '../../store/formula.selector';
import { FormulaItemEntity } from './formula-item.entity';
import { FormulaListEntity } from './formula-list.entity';

@Component({
	selector: 'app-formula-list',
	templateUrl: './formula-list.component.html',
	styleUrls: ['./formula-list.component.scss'],
})
export class FormulaListComponent extends DcBaseComponent implements OnInit {
	protected readonly DcIcons = DcIcons;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public ActionIcons = ActionIcons;
	public panelActions: Array<PanelAction> = [];
	public currentlyHovered = -1;
	public toggleActions = false;
	public selectionCount = 0;

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	public vo$: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		list: FormulaListEntity;
	}>;

	public constructor(
		private readonly store: Store,
		private readonly formulaSelector: FormulaSelector,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'formula-list';
		this.dxLocalStorageKey = 'dx.grid.formula-list';

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			isLoading: this.formulaSelector.getLoading$(),
			list: this.formulaSelector.getFormulaList$(),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.store.dispatch(initFormulaList());
		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();
	}

	public refresh(): void {
		this.firstLoadSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshFormulaList());
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}
	public onSelectionChange($event: SelectionChangedEvent): void {
		this.toggleActions = $event.selectedRowsData.length > 0;
		this.selectionCount = $event.selectedRowsData.length;
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.firstLoadSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public createNewFormula(): void {}

	public editFormula(formula: FormulaItemEntity): void {}

	public duplicateFormula(formula: FormulaItemEntity): void {
		this.store.dispatch(
			openDuplicateConfirmationPopup({
				formulaId: [formula.id],
				formulaLabel: formula.label,
			})
		);
	}

	public deleteFormula(formula: FormulaItemEntity): void {
		if (!formula.isOwner) {
			return;
		}
		this.store.dispatch(
			openDeleteConfirmationPopup({
				formulaId: [formula.id],
				formulaLabel: formula.label,
			})
		);
	}
	public exportFormula(formula: FormulaItemEntity): void {}

	public updateIsShared(formula: FormulaItemEntity, checked: boolean): void {
		if (!formula.isOwner) {
			return;
		}
		this.store.dispatch(
			toggleFormulaSharedStatus({
				formulaId: formula.id,
				shared: checked,
			})
		);
	}

	private async deleteSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedFormulas = await firstValueFrom(
			this.formulaSelector.getSelectedFormulas$(selectedRowKeys)
		);
		this.grid.instance.clearSelection();
		this.selectionCount = 0;
		if (selectedFormulas.length === 1) {
			return this.store.dispatch(
				openDeleteConfirmationPopup({
					formulaId: [selectedFormulas[0].id],
					formulaLabel: selectedFormulas[0].label,
				})
			);
		}
		return this.store.dispatch(
			openDeleteConfirmationPopup({
				formulaId: selectedFormulas.map((k) => k.id),
				formulaLabel: '',
			})
		);
	}
	private exportSelected(): void {}
	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.ColumnExpositionChooser,
			ActionIcons.Delete,
			ActionIcons.Filter,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@formula.list.columnSelection:`,
				typed: '',
			},
			{
				order: 1,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@formula.list.deleteSelection:`,
				typed: 'plain-context-action',
			},
			{
				order: 2,
				command: (): void => this.exportSelected(),
				iconName: actionMap.get(ActionIcons.ExportDatablock)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@formula.list.exportSelection:`,
				typed: 'plain-context-action',
			},
		];
	}
}
